/**
 * STATUS: UNFINISHED (unarmed combat is a WIP prototype, currently DISABLED in play). RMB+unarmed is
 * a plain slow walk right now — see `UNARMED_COMBAT_ENABLED` in main.ts. This table + the avatar
 * move/block/parry path + the parry-knockback netcode all stay intact for when it's resumed.
 *
 * Unarmed-combat MOVE TABLE + pure decision logic. Two fight buttons — E = punch, Q = kick — each
 * with four DIRECTIONAL variants (forward / back / left / right) picked from the player's movement
 * input. Each variant has its own clip, damage, reach, recovery, and movement profile. PUNCHES are
 * upper-body ("upper") so the legs keep their locomotion (top/bottom decoupled); KICKS are full-body.
 * Pure + deterministic → unit-tested (client/test/moves.test.mts).
 */
export type MoveKind = "punch" | "kick";
export type Dir = "f" | "b" | "l" | "r";
export type MoveId = `${MoveKind}_${Dir}`;
export type MoveWeight = "light" | "heavy";

/** Brief travel applied by the controller during the move: a dash IMPULSE of `speed` along the local
 *  (fwd, right) axes that coasts under ground friction (~speed×0.09 m of net travel — same units as the
 *  evade dash, EVADE_SPEED 28 ≈ 2.4 m). Lets each direction read distinctly (forward lunges in, back
 *  hops out, …). Punches keep their locomotion on top (decoupled); kicks root and ride the dash. */
export interface MoveProfile {
  fwd: number;   // -1..1
  right: number; // -1..1
  speed: number; // dash impulse (coasts under friction)
}

export interface MoveDef {
  id: MoveId;
  kind: MoveKind;
  dir: Dir;
  label: string;
  clip: string;            // dh_* clip key (resolveMove gives the id; avatar plays MOVES[id].clip)
  layer: "upper" | "full"; // punch = masked upper overlay (no root); kick = full-body one-shot
  weight: MoveWeight;      // light (punch) opens a cancel window; heavy (kick) is a cancel target
  dmg: number;
  reach: number;           // m — cone reach
  arc: number;             // rad — cone half-angle
  recovery: number;        // s — lockout the move occupies
  move: MoveProfile;
}

const PUNCH_MOVE: Record<Dir, MoveProfile> = {
  f: { fwd: 1, right: 0, speed: 6 },   // small step in (~0.5 m) on top of locomotion
  b: { fwd: -1, right: 0, speed: 5 },
  l: { fwd: 0, right: -1, speed: 5 },
  r: { fwd: 0, right: 1, speed: 5 },
};
const KICK_MOVE: Record<Dir, MoveProfile> = {
  f: { fwd: 1, right: 0, speed: 15 },  // lunge in (~1.3 m)
  b: { fwd: -1, right: 0, speed: 10 }, // hop back
  l: { fwd: 0, right: -1, speed: 13 },
  r: { fwd: 0, right: 1, speed: 13 },
};

/** Starting values — all tunable. Punches: fast, light, upper-body (you keep moving). Kicks: slower,
 *  heavier, longer reach, full-body. forward variants lunge / hit hardest; back variants are quicker. */
export const MOVES: Record<MoveId, MoveDef> = {
  punch_f: { id: "punch_f", kind: "punch", dir: "f", label: "Punch (fwd)",   clip: "dh_punch_f", layer: "upper", weight: "light", dmg: 8, reach: 2.5, arc: 1.0, recovery: 0.30, move: PUNCH_MOVE.f },
  punch_b: { id: "punch_b", kind: "punch", dir: "b", label: "Punch (back)",  clip: "dh_punch_b", layer: "upper", weight: "light", dmg: 5, reach: 2.2, arc: 1.0, recovery: 0.28, move: PUNCH_MOVE.b },
  punch_l: { id: "punch_l", kind: "punch", dir: "l", label: "Punch (left)",  clip: "dh_punch_l", layer: "upper", weight: "light", dmg: 7, reach: 2.3, arc: 1.1, recovery: 0.34, move: PUNCH_MOVE.l },
  punch_r: { id: "punch_r", kind: "punch", dir: "r", label: "Punch (right)", clip: "dh_punch_r", layer: "upper", weight: "light", dmg: 7, reach: 2.3, arc: 1.1, recovery: 0.34, move: PUNCH_MOVE.r },
  kick_f:  { id: "kick_f",  kind: "kick",  dir: "f", label: "Kick (fwd)",    clip: "dh_kick_f",  layer: "full",  weight: "heavy", dmg: 14, reach: 3.0, arc: 0.9, recovery: 0.55, move: KICK_MOVE.f },
  kick_b:  { id: "kick_b",  kind: "kick",  dir: "b", label: "Kick (back)",   clip: "dh_kick_b",  layer: "full",  weight: "heavy", dmg: 10, reach: 2.6, arc: 0.9, recovery: 0.50, move: KICK_MOVE.b },
  kick_l:  { id: "kick_l",  kind: "kick",  dir: "l", label: "Kick (left)",   clip: "dh_kick_l",  layer: "full",  weight: "heavy", dmg: 13, reach: 2.8, arc: 0.9, recovery: 0.55, move: KICK_MOVE.l },
  kick_r:  { id: "kick_r",  kind: "kick",  dir: "r", label: "Kick (right)",  clip: "dh_kick_r",  layer: "full",  weight: "heavy", dmg: 13, reach: 2.8, arc: 0.9, recovery: 0.55, move: KICK_MOVE.r },
};

// (dirFromMove / resolveMove / BIND_TO_KIND — the PLAYER's bind→move input resolution — deleted
//  2026-07-23 with the dead unarmed input path. MOVES + canCancelInto stay: humanoid
//  drones and remote brawlers play these ids via avatar.move()/the wire.)

/** Seconds a hit-confirmed punch stays cancellable into a kick (recovery-cancel). Tunable. */
export const CANCEL_WINDOW = 0.25;

/**
 * Can `next` recovery-cancel the move currently in recovery? Only a PUNCH that LANDED, still inside
 * its cancel window, cancels — and only INTO a kick. Kicks don't chain; whiffs open no window.
 */
export function canCancelInto(
  active: { id: MoveId; hitConfirmed: boolean; cancelT: number } | null,
  next: MoveId,
): boolean {
  if (!active) return false;
  if (MOVES[active.id].weight !== "light") return false;
  if (!active.hitConfirmed || active.cancelT <= 0) return false;
  return MOVES[next].weight === "heavy";
}
